import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { SPECIAL_TEAMS_POSITIONS } from '../data/sportsPositions';
import { Position, DepthChartEntry, Player } from '../types';
import { DepthChartTab } from '../data/sportRulesets';
import { isAndroid, isMobile } from '../utils/mobile';

interface PrintData {
  currentTeam: any;
  sportRuleset: any;
  depthChart: DepthChartEntry[];
  players: Player[];
  depthStrings: Record<string, { id: number; name: string; active: boolean }[]>;
  customPositions: Position[];
}

interface UseDepthChartPrintReturn {
  handlePrint: () => Promise<void>;
}

export const useDepthChartPrint = (data: PrintData): UseDepthChartPrintReturn => {
  const { currentTeam, sportRuleset, depthChart, players, depthStrings, customPositions } = data;

  const handlePrint = useCallback(async () => {
    // This function now prints ALL depth charts from ALL tabs, not just the active tab
    if (!currentTeam || !sportRuleset) return;

    // NOTE: On Android native (Capacitor) `window.open('', '_blank')` can launch Chrome
    // without carrying over the in-app document context, resulting in a blank page.
    // For Android native, we open a self-contained `data:` URL instead.
    const printWindow = !isMobile() || !isAndroid() ? window.open('', '_blank') : null;
    if (!isMobile() || !isAndroid()) {
      if (!printWindow) {
        console.error('Failed to open print window');
        return;
      }
      // Force clear any cached content
      printWindow.document.open();
      printWindow.document.write('');
      printWindow.document.close();
    }

    // Load ALL depth chart data for ALL tabs, not just the currently active one
    let allDepthChartData: DepthChartEntry[] = [];
    
    try {
      console.log('[Print] Loading all depth chart data from database...');
      
      // Get all available tabs from the sport ruleset
      const tabsToShow = sportRuleset.tabs.map((tab: DepthChartTab) => tab.id);
      
      // Load data for all tabs
      const { data: allData, error } = await supabase
        .from('depth_chart')
        .select('*')
        .eq('team_id', currentTeam.id)
        .in('unit', tabsToShow);
      
      if (error) {
        console.error('[Print] Error loading depth chart data:', error);
        printWindow?.close();
        return;
      }
      
      allDepthChartData = allData || [];
      console.log('[Print] Loaded depth chart data for all tabs:', allDepthChartData.length, 'entries');
    } catch (error) {
      console.error('[Print] Error loading data:', error);
      printWindow?.close();
      return;
    }

    // Get tabs from sport ruleset
    const tabsToShow = sportRuleset.tabs.map((tab: DepthChartTab) => tab.id);
    const unitData: Record<string, { positions: Position[], entries: DepthChartEntry[], innings?: number[] }> = {};
    
    // Organize data by tabs from sport ruleset
    tabsToShow.forEach((tabId: string) => {
      const dbUnit = tabId;
      const tabEntries = allDepthChartData.filter(entry => entry.unit === dbUnit);
      let tabPositions: Position[] = [];
      
      // Handle Special Teams differently for Football
      if (tabId === 'Special Teams' && sportRuleset.showSpecialTeams) {
        // Get all positions that have entries
        const uniquePositionKeys = Array.from(new Set(tabEntries.map(entry => entry.position)));
        tabPositions = uniquePositionKeys.map(positionKey => {
          const customPosition = customPositions.find(cp => cp.key === positionKey);
          if (customPosition) {
            return {
              key: positionKey,
              name: customPosition.name,
              fullName: customPosition.fullName,
              custom: true,
              color: ''
            };
          }
          return { key: positionKey, name: positionKey, fullName: positionKey, color: '' };
        });
      } else {
        // For regular tabs, we would need access to formations data
        // This is a simplified version - in real implementation, we'd need formation data
        const uniquePositionKeys = Array.from(new Set(tabEntries.map(entry => entry.position)));
        tabPositions = uniquePositionKeys.map(positionKey => {
          const customPosition = customPositions.find(cp => cp.key === positionKey);
          if (customPosition) {
            return {
              key: positionKey,
              name: customPosition.name,
              fullName: customPosition.fullName,
              custom: true,
              color: ''
            };
          }
          return { key: positionKey, name: positionKey, fullName: positionKey, color: '' };
        });
      }
      
      unitData[tabId] = {
        positions: tabPositions,
        entries: tabEntries
      };
    });

    // Create main content container
    const mainContent = document.createElement('div');
    mainContent.className = 'main-content';

    // Handle different sport layouts - now all sports will show all tabs
    if (currentTeam.sport === 'Football') {
      // Special layout for Football with all tabs
      createFootballPrintLayout(mainContent, unitData, players, depthStrings, sportRuleset);
    } else if (currentTeam.sport === 'Baseball' || currentTeam.sport === 'Softball') {
      // Special layout for Baseball/Softball with combined lineup/fielding table
      createBaseballPrintLayout(
        mainContent,
        unitData,
        players,
        depthStrings,
        currentTeam,
        sportRuleset?.defaultInnings
      );
    } else {
      // Standard layout for all other sports - show all tabs
      createStandardPrintLayout(mainContent, unitData, tabsToShow, players, depthStrings, sportRuleset);
    }

    // Create the print container
    const printContainer = document.createElement('div');
    const isBaseballLike = currentTeam.sport === 'Baseball' || currentTeam.sport === 'Softball';
    printContainer.className = `print-container${isBaseballLike ? ' print-container--baseball' : ''}`;

    // Add header
    const header = document.createElement('div');
    header.className = 'header';
    
    // For Baseball/Softball, use simplified header
    if (currentTeam.sport === 'Baseball' || currentTeam.sport === 'Softball') {
      header.innerHTML = `
        <h1>${currentTeam.name} - Lineup</h1>
      `;
    } else {
      header.innerHTML = `
        <h1>${currentTeam.name} - Depth Chart</h1>
        <div class="team-info">
          <span>Sport: ${currentTeam.sport}</span>
          ${currentTeam.offensiveScheme ? `<span>Offensive Scheme: ${currentTeam.offensiveScheme}</span>` : ''}
          ${currentTeam.defensiveScheme ? `<span>Defensive Scheme: ${currentTeam.defensiveScheme}</span>` : ''}
        </div>
      `;
    }
    printContainer.appendChild(header);
    printContainer.appendChild(mainContent);

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${currentTeam.name} - Depth Chart</title>
          ${getPrintStyles()}
          <style>
            /* In-app (WebView) printing needs an explicit user gesture on Android. */
            .print-toolbar {
              position: sticky;
              top: 0;
              z-index: 9999;
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 12px;
              padding: 10px 12px;
              background: rgba(255, 255, 255, 0.96);
              border-bottom: 1px solid rgba(0, 0, 0, 0.08);
              backdrop-filter: blur(8px);
            }
            .print-toolbar__title {
              font-size: 14px;
              font-weight: 600;
              color: #111827;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .print-toolbar__actions {
              display: flex;
              gap: 8px;
              flex-shrink: 0;
            }
            .print-toolbar__btn {
              appearance: none;
              border: 1px solid rgba(0, 0, 0, 0.14);
              background: #ffffff;
              color: #111827;
              font-size: 13px;
              font-weight: 600;
              padding: 8px 10px;
              border-radius: 10px;
            }
            .print-toolbar__hint {
              font-size: 12px;
              color: #6b7280;
            }
            @media print {
              .print-toolbar { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div class="print-toolbar">
            <div style="min-width:0">
              <div class="print-toolbar__title">${currentTeam.name} — Depth Chart</div>
              <div class="print-toolbar__hint">Tap Print to open the system print / Save as PDF dialog.</div>
            </div>
            <div class="print-toolbar__actions">
              <button class="print-toolbar__btn" onclick="window.print()" type="button">Print</button>
            </div>
          </div>
          ${printContainer.innerHTML}
          <script>
            window.SPECIAL_TEAMS_POSITIONS = ${JSON.stringify(SPECIAL_TEAMS_POSITIONS)};
            console.log('Print window opened with updated CSS at ${new Date().toISOString()}');
            window.addEventListener('load', () => {
              // Best-effort auto-print (often blocked in Android WebView without a user gesture).
              setTimeout(() => {
                try { window.print(); } catch {}
              }, 250);
            });
          </script>
        </body>
      </html>
    `;

    // Android native printing: use native print dialog (includes Save as PDF).
    // `window.print()` is unreliable in Android WebView even with user gestures.
    if (isMobile() && isAndroid()) {
      try {
        const { Printer } = await import('@bcyesil/capacitor-plugin-printer');
        // API is `Printer.print({ content, name? })` (HTML content supported).
        await (Printer as any).print({ content: html, name: `${currentTeam.name} - Depth Chart` });
        return;
      } catch (e) {
        console.error('Native print failed, falling back to in-app view:', e);
        // Fall through to the in-app blob view fallback
      }
    }

    if (isMobile() && isAndroid()) {
      // Android/Chrome can block top-frame navigation to `data:` URLs from WebView.
      // Use a self-contained `blob:` URL instead.
      try {
        const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
        const w = window.open(blobUrl, '_blank');
        if (!w) console.error('Failed to open print window');
        // Best-effort cleanup after the new tab has had time to load.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      } catch (e) {
        console.error('Failed to open print window:', e);
      }
      return;
    }

    // Write content and styles to print window (web / non-Android native)
    printWindow!.document.write(html);
    printWindow!.document.close();
    // Fallback print trigger (some browsers don't fire load for about:blank doc writes)
    setTimeout(() => {
      try {
        printWindow!.print();
      } catch {}
    }, 500);
  }, [currentTeam, sportRuleset, depthChart, players, depthStrings, customPositions]);

  return { handlePrint };
};

// Helper functions for different sport layouts
function createBaseballPrintLayout(
  mainContent: HTMLElement, 
  unitData: Record<string, any>, 
  players: Player[], 
  depthStrings: Record<string, any>,
  currentTeam?: any,
  rulesetDefaultInnings?: number
) {
  const fieldingEntries: DepthChartEntry[] = unitData['Fielding']?.entries || [];
  const maxDepthFromChart =
    fieldingEntries.length > 0
      ? Math.max(...fieldingEntries.map(e => Number(e.depth) || 0), 0)
      : 0;
  const fallbackConfigured = rulesetDefaultInnings ?? 9;
  const teamInningsNum = Number(currentTeam?.innings);
  const teamConfigured =
    Number.isFinite(teamInningsNum) && teamInningsNum > 0
      ? teamInningsNum
      : fallbackConfigured;
  // Team setting / ruleset default, expanded if saved fielding rows go deeper (e.g. extra innings)
  const totalInnings = Math.max(teamConfigured, maxDepthFromChart, 1);

  // Create the combined lineup/fielding table
  const table = document.createElement('table');
  table.className = 'combined-lineup-fielding-table';
  table.style.width = '100%';
  table.style.borderCollapse = 'collapse';

  // Create table header
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  
  // Lineup column header
  const lineupHeader = document.createElement('th');
  lineupHeader.className = 'lineup-player-cell';
  lineupHeader.textContent = 'Batting Order';
  lineupHeader.style.border = '2px solid #333';
  lineupHeader.style.padding = '12px 16px';
  lineupHeader.style.textAlign = 'left';
  headerRow.appendChild(lineupHeader);

  // Inning headers
  for (let inning = 1; inning <= totalInnings; inning++) {
    const inningHeader = document.createElement('th');
    inningHeader.className = 'fielding-position-cell';
    inningHeader.textContent = `Inning ${inning}`;
    inningHeader.style.border = '2px solid #333';
    inningHeader.style.padding = '12px 8px';
    inningHeader.style.textAlign = 'center';
    headerRow.appendChild(inningHeader);
  }

  thead.appendChild(headerRow);
  table.appendChild(thead);

  // Create table body
  const tbody = document.createElement('tbody');

  // Get lineup data (batting order)
  const lineupData = unitData['Lineup'] || { entries: [] };
  const fieldingData = unitData['Fielding'] || { entries: [] };

  // Get all players in batting order (sorted by position which is the batting order number)
  const lineupEntries = lineupData.entries || [];
  const sortedLineup = [...lineupEntries].sort((a, b) => {
    const orderA = parseInt(a.position) || 999;
    const orderB = parseInt(b.position) || 999;
    return orderA - orderB;
  });

  // Create rows for each player in the lineup
  sortedLineup.forEach((lineupEntry) => {
    const player = players.find(p => p.id === lineupEntry.player_id);
    if (!player) return;

    const row = document.createElement('tr');

    // Lineup cell (batting order)
    const lineupCell = document.createElement('td');
    lineupCell.className = 'lineup-player-cell';
    lineupCell.style.border = '2px solid #333';
    lineupCell.style.padding = '12px 16px';
    lineupCell.style.textAlign = 'left';
    const playerInfo = document.createElement('div');
    playerInfo.className = 'player-info';
    const battingOrder = parseInt(lineupEntry.position) || 0;
    playerInfo.textContent = `${battingOrder}. ${player.first_name} ${player.last_name}${player.number ? ` (#${player.number})` : ''}`;
    lineupCell.appendChild(playerInfo);
    row.appendChild(lineupCell);

    // Inning cells - show positions for each inning
    for (let inning = 1; inning <= totalInnings; inning++) {
      const inningCell = document.createElement('td');
      inningCell.className = 'fielding-position-cell';
      inningCell.style.border = '2px solid #333';
      inningCell.style.padding = '12px 8px';
      inningCell.style.textAlign = 'center';
      
      // Find all positions this player is playing in this inning
      const positionsForInning = fieldingData.entries?.filter(
        (entry: DepthChartEntry) => entry.player_id === player.id && entry.depth === inning
      ) || [];
      
      if (positionsForInning.length > 0) {
        const positionsText = positionsForInning
          .map((entry: DepthChartEntry) => entry.position)
          .join(', ');
        const positionDiv = document.createElement('div');
        positionDiv.className = 'fielding-position';
        positionDiv.textContent = positionsText;
        inningCell.appendChild(positionDiv);
      } else {
        // Lineup player sitting out this inning — solid red cell for print clarity
        inningCell.classList.add('fielding-sit-out');
        inningCell.title = 'Sitting out';
      }

      row.appendChild(inningCell);
    }

    tbody.appendChild(row);
  });

  table.appendChild(tbody);
  mainContent.appendChild(table);
}

function createFootballPrintLayout(
  mainContent: HTMLElement,
  unitData: Record<string, any>,
  players: Player[],
  depthStrings: Record<string, any>,
  sportRuleset: any
) {
  // Create main units row (Offense, Defense)
  const mainUnitsRow = document.createElement('div');
  mainUnitsRow.className = 'main-units-row';
  
  const mainUnits = ['Offense', 'Defense'];
  
  mainUnits.forEach(tabId => {
    if (unitData[tabId] && unitData[tabId].positions.length > 0) {
      const unitSection = createFootballUnitSection(tabId, unitData[tabId], players, depthStrings, sportRuleset);
      mainUnitsRow.appendChild(unitSection);
    }
  });
  
  mainContent.appendChild(mainUnitsRow);
  
  // Handle Special Teams separately
  if (unitData['Special Teams']) {
    const specialTeamsSection = createSpecialTeamsSection(unitData['Special Teams'], players);
    mainContent.appendChild(specialTeamsSection);
  }
}

function createStandardPrintLayout(
  mainContent: HTMLElement,
  unitData: Record<string, any>,
  tabsToShow: string[],
  players: Player[],
  depthStrings: Record<string, any>,
  sportRuleset: any
) {
  tabsToShow.forEach(tabId => {
    const tabSection = document.createElement('div');
    tabSection.className = 'tab-section';

    const tabHeader = document.createElement('h2');
    tabHeader.className = 'tab-header';
    tabHeader.textContent = tabId;
    tabSection.appendChild(tabHeader);

    if (!unitData[tabId] || unitData[tabId].positions.length === 0) {
      const noData = document.createElement('div');
      noData.className = 'no-data';
      noData.textContent = 'No positions configured for this unit';
      tabSection.appendChild(noData);
    } else {
      const table = createStandardTable(unitData[tabId], tabId, players, depthStrings, sportRuleset);
      tabSection.appendChild(table);
    }

    mainContent.appendChild(tabSection);
  });
}

function createFootballUnitSection(
  tabId: string,
  unitData: any,
  players: Player[],
  depthStrings: Record<string, any>,
  sportRuleset: any
): HTMLElement {
  const unitSection = document.createElement('div');
  unitSection.className = 'unit-section';
  
  const unitHeader = document.createElement('h3');
  unitHeader.className = 'unit-header';
  unitHeader.textContent = tabId;
  unitSection.appendChild(unitHeader);
  
  const table = createStandardTable(unitData, tabId, players, depthStrings, sportRuleset, true);
  table.className = 'positions-table main-unit-table';
  unitSection.appendChild(table);
  
  return unitSection;
}

function createSpecialTeamsSection(specialTeamsData: any, players: Player[]): HTMLElement {
  const specialSection = document.createElement('div');
  specialSection.className = 'other-special-teams-section';
  
  const header = document.createElement('h3');
  header.textContent = 'Special Teams';
  header.className = 'unit-header';
  specialSection.appendChild(header);
  
  // Implementation would continue here
  return specialSection;
}

function createStandardTable(
  unitData: any,
  tabId: string,
  players: Player[],
  depthStrings: Record<string, any>,
  sportRuleset: any,
  isCompact = false
): HTMLTableElement {
  const table = document.createElement('table');
  table.className = isCompact ? 'positions-table main-unit-table' : 'positions-table';

  // Create header
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  
  const positionHeader = document.createElement('th');
  positionHeader.textContent = 'Position';
  headerRow.appendChild(positionHeader);

  // Get depth strings
  let tabDepthStrings = depthStrings[tabId] || [];
  if (tabDepthStrings.length === 0) {
    tabDepthStrings = sportRuleset.depthStrings.defaultLabels.map((label: any) => ({
      ...label,
      active: true
    }));
  }
  
  const activeDepthStrings = tabDepthStrings.filter((ds: any) => ds.active);
  
  activeDepthStrings.forEach((ds: any) => {
    const depthHeader = document.createElement('th');
    depthHeader.textContent = ds.name;
    headerRow.appendChild(depthHeader);
  });

  thead.appendChild(headerRow);
  table.appendChild(thead);

  // Create body - Group positions by color
  const tbody = document.createElement('tbody');
  
    // Get all unique positions from the unit data
  const allPositions = new Set<string>();
  unitData.entries.forEach((entry: DepthChartEntry) => {
    allPositions.add(entry.position);
  });
  
  // Create position data with color info for sorting
  const positionData: Array<{position: string, firstColor: string | null}> = [];
  
  Array.from(allPositions).forEach(position => {
    // Find the first depth entry for this position
    const firstDepthEntry = unitData.entries.find((entry: DepthChartEntry) => 
      entry.position === position && entry.depth === activeDepthStrings[0]?.id
    );
    
    positionData.push({
      position: position,
      firstColor: firstDepthEntry?.color || null
    });
  });
  
  // Sort: positions with colors first (grouped by color), then positions without colors alphabetically
  positionData.sort((a, b) => {
    // If both have colors, sort by color then position
    if (a.firstColor && b.firstColor) {
      if (a.firstColor === b.firstColor) {
        return a.position.localeCompare(b.position);
      }
      return a.firstColor.localeCompare(b.firstColor);
    }
    // If only one has color, put colored ones first
    if (a.firstColor && !b.firstColor) return -1;
    if (!a.firstColor && b.firstColor) return 1;
    // If neither has color, sort alphabetically
    return a.position.localeCompare(b.position);
  });
  
  const sortedPositions = positionData.map(item => item.position);
  
  // Create one row per position, with each column colored individually
  sortedPositions.forEach(position => {
    const row = document.createElement('tr');
    row.className = 'color-group-row';
    
    const positionCell = document.createElement('td');
    positionCell.className = 'position-cell';
    positionCell.innerHTML = `
      <div class="position-name">${position}</div>
    `;
    row.appendChild(positionCell);

    activeDepthStrings.forEach((ds: any) => {
      const playerCell = document.createElement('td');
      playerCell.className = 'player-cell';
      
      // Find the entry for this specific position and depth
      const entry = unitData.entries.find((e: DepthChartEntry) => 
        e.position === position && e.depth === ds.id
      );
      
      if (entry && entry.player_id) {
        const player = players.find(p => p.id === entry.player_id);
        if (player) {
          playerCell.innerHTML = `
            <div class="player-name">${player.first_name} ${player.last_name}</div>
            <div class="player-number">#${player.number}</div>
          `;
          
          // Apply the color styling based on this specific entry's color
          if (entry.color && entry.color.trim() !== '') {
            playerCell.style.backgroundColor = `${entry.color}30`;
            playerCell.style.borderLeft = `3px solid ${entry.color}`;
          }
        }
      }
      
      row.appendChild(playerCell);
    });

    tbody.appendChild(row);
  });

    // Note: We're only showing colored entries now, so no "no color" section needed

  table.appendChild(tbody);
  return table;
}

function getPrintStyles(): string {
  return `
    <style>
      /* Use named pages so landscape is opt-in */
      @page baseballLandscape {
        size: landscape;
        margin: 0.5in;
      }
      @page portraitDefault {
        size: letter portrait;
        margin: 0.5in;
      }
      .print-container {
        page: portraitDefault;
      }
      .print-container.print-container--baseball {
        page: baseballLandscape;
      }

      /* Baseball/softball: sitting out — show as red block on screen and when printing */
      .combined-lineup-fielding-table td.fielding-position-cell.fielding-sit-out {
        background-color: #fca5a5 !important;
        min-height: 1.25em;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
      @media print {
        body {
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
          font-size: 10pt;
          line-height: 1.2;
        }
        
        .print-container {
          width: 100%;
          height: 100vh;
          display: flex;
          flex-direction: column;
        }
        
        .header {
          text-align: center;
          margin-bottom: 15px;
          border-bottom: 2px solid #000;
          padding-bottom: 8px;
        }
        
        .header h1 {
          margin: 0 0 5px 0;
          font-size: 16pt;
          font-weight: bold;
        }
        
        .team-info {
          font-size: 9pt;
          color: #666;
        }
        
        .main-content {
          display: flex;
          flex-direction: column;
          flex: 1;
          gap: 10px;
        }
        
        .tab-section {
          margin-bottom: 15px;
          page-break-inside: avoid;
        }
        
        .tab-header {
          font-size: 12pt;
          font-weight: bold;
          text-align: center;
          margin: 0 0 8px 0;
          padding: 4px;
          background-color: #f0f0f0;
          border: 1px solid #000;
        }
        
        .no-data {
          text-align: center;
          padding: 10px;
          color: #666;
          font-style: italic;
          border: 1px dashed #ccc;
          background-color: #f9f9f9;
        }
        
        .main-units-row {
          display: flex;
          flex-wrap: nowrap;
          gap: 8px;
          margin-bottom: 15px;
          width: 100%;
        }
        
        .unit-section {
          flex: 1;
          min-width: 120px;
          max-width: 23%;
        }
        
        .unit-header {
          font-size: 10pt;
          font-weight: bold;
          text-align: center;
          margin: 0 0 5px 0;
          padding: 3px;
          background-color: #f0f0f0;
          border: 1px solid #000;
        }
        
        .positions-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 7pt;
          margin-bottom: 5px;
        }
        
        .positions-table th,
        .positions-table td {
          border: 1px solid #333;
          padding: 1px 2px;
          text-align: center;
          vertical-align: middle;
        }
        
        .main-unit-table {
          font-size: 6pt;
        }
        
        .position-cell {
          text-align: left;
          padding: 1px 2px;
        }
        
        .position-name {
          font-weight: bold;
          font-size: 7pt;
        }
        
        .player-cell {
          font-size: 7pt;
        }
        
        .player-name {
          font-weight: bold;
        }
        
        .player-number {
          font-size: 6pt;
          color: #666;
        }
        
        .color-group-header {
          page-break-inside: avoid;
        }
        
        .color-group-header-cell {
          font-size: 8pt;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .color-group-row {
          page-break-inside: avoid;
        }
      }
      
      @media screen {
        body {
          font-family: Arial, sans-serif;
          padding: 20px;
        }
        
        .positions-table {
          font-size: 12px;
        }
        
        .positions-table th,
        .positions-table td {
          padding: 6px;
          border: 1px solid #ccc;
        }
      }
    </style>
  `;
}